<script setup lang="ts">
import type { Rule } from "async-validator";
import { storeToRefs } from "pinia";
import {
  NotificationKey,
  showNotificationSuccess,
} from "~/core/utils/notifications";
import { useUpdateGlobalDeviceConfiguration } from "~/modules/globalDeviceConfiguration/composables/useUpdateGlobalDeviceConfiguration";
import {
  AUTOMATIC_RENEWAL_DELAY_MAX,
  AUTOMATIC_RENEWAL_DELAY_MIN,
  CUSTOM_VALUE,
  POLLING_FREQUENCY_MAX,
  POLLING_FREQUENCY_MIN,
} from "~/modules/globalDeviceConfiguration/constants";
import type { GlobalDeviceConfiguration } from "~/modules/globalDeviceConfiguration/models/GlobalDeviceConfiguration.interface";
import type { Option } from "~/modules/globalDeviceConfiguration/models/Option.interface";
import { addOption } from "~/modules/globalDeviceConfiguration/utils/addOption";
import { useOrganizationStore } from "~/modules/organization/stores/useOrganizationStore";
import ILxcSave from "~icons/lxc/save";

const props = defineProps<{
  globalDeviceConfiguration: GlobalDeviceConfiguration;
}>();

const { t } = useI18n();

const { currentOrganization } = storeToRefs(useOrganizationStore());

const {
  error: errorUpdateGlobalConfiguration,
  updateGlobalDeviceConfiguration,
} = useUpdateGlobalDeviceConfiguration({
  organizationCode: currentOrganization.value?.code,
});

const globalDeviceConfigurationComputed = computed({
  get() {
    return props.globalDeviceConfiguration;
  },
  set() {},
});

/** Init options */
const AUTOMATIC_RENEWAL_DELAY_DAY_KEY =
  "dtwins.globalConfiguration.automaticRenewalDelay.values.nDay";

const AUTOMATIC_RENEWAL_DELAY_MONTH_KEY =
  "dtwins.globalConfiguration.automaticRenewalDelay.values.nMonth";

const AUTOMATIC_RENEWAL_DELAY_YEAR_KEY =
  "dtwins.globalConfiguration.automaticRenewalDelay.values.nYear";

const AUTOMATIC_RENEWAL_DELAY_CUSTOM_KEY =
  "dtwins.globalConfiguration.automaticRenewalDelay.custom.label";

globalDeviceConfigurationComputed.value.automaticRenewalDelayOptions = [
  { value: 30, text: t(AUTOMATIC_RENEWAL_DELAY_MONTH_KEY, { n: 1 }) },
  { value: 90, text: t(AUTOMATIC_RENEWAL_DELAY_MONTH_KEY, { n: 3 }) },
  { value: 183, text: t(AUTOMATIC_RENEWAL_DELAY_MONTH_KEY, { n: 6 }) },
  { value: 365, text: t(AUTOMATIC_RENEWAL_DELAY_YEAR_KEY, { n: 1 }) },
  { value: 1098, text: t(AUTOMATIC_RENEWAL_DELAY_YEAR_KEY, { n: 3 }) },
  { value: CUSTOM_VALUE, text: t(AUTOMATIC_RENEWAL_DELAY_CUSTOM_KEY) },
];

// add initial automaticRenewalDelay option if it does not exist
addOptionByValueAndTextKey(
  globalDeviceConfigurationComputed.value.automaticRenewalDelayOptions,
  globalDeviceConfigurationComputed.value.automaticRenewalDelayInitial,
  AUTOMATIC_RENEWAL_DELAY_DAY_KEY,
);

// set current and selected automaticRenewalDelay
globalDeviceConfigurationComputed.value.automaticRenewalDelayCurrent =
  globalDeviceConfigurationComputed.value.automaticRenewalDelaySelected =
    globalDeviceConfigurationComputed.value.automaticRenewalDelayInitial;

const POLLING_FREQUENCY_MINUTE_KEY =
  "dtwins.globalConfiguration.pollingFrequency.values.nMinute";

const POLLING_FREQUENCY_HOUR_KEY =
  "dtwins.globalConfiguration.pollingFrequency.values.nHour";

const POLLING_FREQUENCY_CUSTOM_KEY =
  "dtwins.globalConfiguration.pollingFrequency.custom.label";

globalDeviceConfigurationComputed.value.pollingFrequencyOptions = [
  { value: 360, text: t(POLLING_FREQUENCY_HOUR_KEY, { n: 6 }) },
  { value: 720, text: t(POLLING_FREQUENCY_HOUR_KEY, { n: 12 }) },
  { value: 1080, text: t(POLLING_FREQUENCY_HOUR_KEY, { n: 18 }) },
  { value: 1440, text: t(POLLING_FREQUENCY_HOUR_KEY, { n: 24 }) },
  { value: CUSTOM_VALUE, text: t(POLLING_FREQUENCY_CUSTOM_KEY) },
];

// add initial pollingFrequency option if it does not exist
addOptionByValueAndTextKey(
  globalDeviceConfigurationComputed.value.pollingFrequencyOptions,
  globalDeviceConfigurationComputed.value.pollingFrequencyInitial,
  POLLING_FREQUENCY_MINUTE_KEY,
);

// set current and selected automaticRenewalDelay
globalDeviceConfigurationComputed.value.pollingFrequencyCurrent =
  globalDeviceConfigurationComputed.value.pollingFrequencySelected =
    globalDeviceConfigurationComputed.value.pollingFrequencyInitial;

/**
 * Add an option in option list if it does not exist
 * @param options
 * @param value
 * @param text
 */
function addOptionByValueAndTextKey(
  options?: Option[],
  value?: number,
  text?: string,
) {
  if (!options) {
    options = [];
  }

  if (value !== undefined && value !== null && text) {
    addOption({ value, text: t(text, { n: value }) }, options);
  }
}

/** Init rules */
const automaticRenewalDelayRules: Rule[] = [
  {
    type: "integer",
    required: true,
    message: t(
      "dtwins.globalConfiguration.automaticRenewalDelay.error.required",
    ),
  },
  {
    type: "integer",
    min: AUTOMATIC_RENEWAL_DELAY_MIN,
    max: AUTOMATIC_RENEWAL_DELAY_MAX,
    message: t(
      "dtwins.globalConfiguration.automaticRenewalDelay.error.minMax",
      { min: AUTOMATIC_RENEWAL_DELAY_MIN, max: AUTOMATIC_RENEWAL_DELAY_MAX },
    ),
  },
];

const pollingFrequencyRules: Rule[] = [
  {
    type: "integer",
    required: true,
    message: t("dtwins.globalConfiguration.pollingFrequency.error.required"),
  },
  {
    type: "integer",
    min: POLLING_FREQUENCY_MIN,
    max: POLLING_FREQUENCY_MAX,
    message: t("dtwins.globalConfiguration.pollingFrequency.error.minMax", {
      min: POLLING_FREQUENCY_MIN,
      max: POLLING_FREQUENCY_MAX,
    }),
  },
];

/** Automatic renewal delay validate and cancel custom */
function onValidateAutomaticRenewalDelayCustom(
  globalDeviceConfiguration: GlobalDeviceConfiguration,
) {
  // add the custom option in option list if it does not exist
  addOptionByValueAndTextKey(
    globalDeviceConfiguration.automaticRenewalDelayOptions,
    globalDeviceConfiguration.automaticRenewalDelayCustom,
    AUTOMATIC_RENEWAL_DELAY_DAY_KEY,
  );

  // set selected value with custom value
  globalDeviceConfiguration.automaticRenewalDelaySelected =
    globalDeviceConfiguration.automaticRenewalDelayCustom;

  // reset custom value
  globalDeviceConfiguration.automaticRenewalDelayCustom = undefined;
}

function onCancelAutomaticRenewalDelayCustom(
  globalDeviceConfiguration: GlobalDeviceConfiguration,
) {
  // reset selected value
  globalDeviceConfiguration.automaticRenewalDelaySelected =
    globalDeviceConfiguration.automaticRenewalDelayCurrent;

  // reset custom value
  globalDeviceConfiguration.automaticRenewalDelayCustom = undefined;
}

/** Polling frequency validate and cancel custom */
function onValidatePollingFrequencyCustom(
  globalDeviceConfiguration: GlobalDeviceConfiguration,
) {
  // add the custom option in option list if it does not exist
  addOptionByValueAndTextKey(
    globalDeviceConfiguration.pollingFrequencyOptions,
    globalDeviceConfiguration.pollingFrequencyCustom,
    POLLING_FREQUENCY_MINUTE_KEY,
  );

  // set selected value with custom value
  globalDeviceConfiguration.pollingFrequencySelected =
    globalDeviceConfiguration.pollingFrequencyCustom;

  // reset custom value
  globalDeviceConfiguration.pollingFrequencyCustom = undefined;
}

function onCancelPollingFrequencyCustom(
  globalDeviceConfiguration: GlobalDeviceConfiguration,
) {
  // reset selected value
  globalDeviceConfiguration.pollingFrequencySelected =
    globalDeviceConfiguration.pollingFrequencyCurrent;

  // reset custom value
  globalDeviceConfiguration.pollingFrequencyCustom = undefined;
}

/** Global device configuration save and cancel actions */
function disabledSaveGlobalDeviceConfiguration(
  globalDeviceConfiguration: GlobalDeviceConfiguration,
) {
  return (
    globalDeviceConfiguration.isLoading ||
    ((globalDeviceConfiguration.automaticRenewalDelayInitial ===
      globalDeviceConfiguration.automaticRenewalDelayCurrent ||
      globalDeviceConfiguration.automaticRenewalDelaySelected ===
        CUSTOM_VALUE) &&
      (globalDeviceConfiguration.pollingFrequencyInitial ===
        globalDeviceConfiguration.pollingFrequencyCurrent ||
        globalDeviceConfiguration.pollingFrequencySelected === CUSTOM_VALUE))
  );
}

async function onSaveGlobalDeviceConfiguration(
  globalDeviceConfiguration: GlobalDeviceConfiguration,
) {
  globalDeviceConfiguration.isLoading = true;

  await updateGlobalDeviceConfiguration(globalDeviceConfiguration);

  globalDeviceConfiguration.isLoading = false;

  if (errorUpdateGlobalConfiguration.value) {
    errorUpdateGlobalConfiguration.value.notify(NotificationKey.saveError);
  } else {
    showNotificationSuccess(t(NotificationKey.saveSuccess));

    globalDeviceConfiguration.automaticRenewalDelayInitial =
      globalDeviceConfiguration.automaticRenewalDelayCurrent;
    globalDeviceConfiguration.pollingFrequencyInitial =
      globalDeviceConfiguration.pollingFrequencyCurrent;
  }
}

function onCancelGlobalDeviceConfiguration(
  globalDeviceConfiguration: GlobalDeviceConfiguration,
) {
  // reset selected value
  globalDeviceConfiguration.automaticRenewalDelaySelected =
    globalDeviceConfiguration.automaticRenewalDelayInitial;
  globalDeviceConfiguration.pollingFrequencySelected =
    globalDeviceConfiguration.pollingFrequencyInitial;

  // reset custom value
  globalDeviceConfiguration.automaticRenewalDelayCustom = undefined;
  globalDeviceConfiguration.pollingFrequencyCustom = undefined;
}

watch(props.globalDeviceConfiguration, (globalDeviceConfiguration) => {
  // set current value to the selected one if the selected value is not the custom one
  // this enables to store the value before selecting the custom one in order to reset on cancel custom edition
  if (
    globalDeviceConfiguration.automaticRenewalDelaySelected !== CUSTOM_VALUE
  ) {
    globalDeviceConfiguration.automaticRenewalDelayCurrent =
      globalDeviceConfiguration.automaticRenewalDelaySelected;
  }
  if (globalDeviceConfiguration.pollingFrequencySelected !== CUSTOM_VALUE) {
    globalDeviceConfiguration.pollingFrequencyCurrent =
      globalDeviceConfiguration.pollingFrequencySelected;
  }
});
</script>

<template>
  <lxc-card>
    <template #default>
      <h4>{{ globalDeviceConfigurationComputed.friendlyName }}</h4>

      <!-- Automatic renewal delay -->
      <global-device-configuration-list-item-select
        v-model:selected-value="
          globalDeviceConfigurationComputed.automaticRenewalDelaySelected
        "
        v-model:custom-value="
          globalDeviceConfigurationComputed.automaticRenewalDelayCustom
        "
        prop="automaticRenewalDelay"
        :item-label="
          t('dtwins.globalConfiguration.automaticRenewalDelay.label')
        "
        :options="
          globalDeviceConfigurationComputed.automaticRenewalDelayOptions
        "
        :unit-label="
          t('dtwins.globalConfiguration.automaticRenewalDelay.custom.unit', {
            count:
              globalDeviceConfigurationComputed.automaticRenewalDelayCustom,
          })
        "
        :min="AUTOMATIC_RENEWAL_DELAY_MIN"
        :max="AUTOMATIC_RENEWAL_DELAY_MAX"
        :disabled="globalDeviceConfigurationComputed.isLoading"
        :rules="automaticRenewalDelayRules"
        @validate-custom="
          onValidateAutomaticRenewalDelayCustom(
            globalDeviceConfigurationComputed,
          )
        "
        @cancel-custom="
          onCancelAutomaticRenewalDelayCustom(globalDeviceConfigurationComputed)
        "
      />

      <global-device-configuration-list-item-select
        v-model:selected-value="
          globalDeviceConfigurationComputed.pollingFrequencySelected
        "
        v-model:custom-value="
          globalDeviceConfigurationComputed.pollingFrequencyCustom
        "
        prop="pollingFrequency"
        :item-label="t('dtwins.globalConfiguration.pollingFrequency.label')"
        :options="globalDeviceConfigurationComputed.pollingFrequencyOptions"
        :unit-label="
          t('dtwins.globalConfiguration.pollingFrequency.custom.unit', {
            count: globalDeviceConfigurationComputed.pollingFrequencyCustom,
          })
        "
        :min="POLLING_FREQUENCY_MIN"
        :max="POLLING_FREQUENCY_MAX"
        :disabled="globalDeviceConfigurationComputed.isLoading"
        :rules="pollingFrequencyRules"
        @validate-custom="
          onValidatePollingFrequencyCustom(globalDeviceConfigurationComputed)
        "
        @cancel-custom="
          onCancelPollingFrequencyCustom(globalDeviceConfigurationComputed)
        "
      />

      <div class="flex flex-wrap">
        <lxc-button
          class="mr-6 mt-2"
          type="secondary"
          :title="t('button.cancel')"
          :disabled="
            disabledSaveGlobalDeviceConfiguration(
              globalDeviceConfigurationComputed,
            )
          "
          @click.prevent="
            onCancelGlobalDeviceConfiguration(globalDeviceConfigurationComputed)
          "
          >{{ t("button.cancel") }}</lxc-button
        >

        <lxc-button
          class="mt-2"
          type="primary"
          :title="t('button.save')"
          :disabled="
            disabledSaveGlobalDeviceConfiguration(
              globalDeviceConfigurationComputed,
            )
          "
          :icon="ILxcSave"
          :is-loading="globalDeviceConfigurationComputed.isLoading"
          @click.prevent="
            onSaveGlobalDeviceConfiguration(globalDeviceConfigurationComputed)
          "
          >{{ t("button.save") }}</lxc-button
        >
      </div>
    </template>
  </lxc-card>
</template>
